import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  MapPin, Plus, PencilSimpleLine, Trash, Lightning, Warning, UploadSimple,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FixedLocations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // fixed-location row OR null for create
  const [prefill, setPrefill] = useState(null); // {sku_id, variant_id, location_id} from URL
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await axios.get(`${API}/api/fixed-locations`, { withCredentials: true });
      setItems(data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load fixed locations');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Deep-link support — e.g. /fixed-locations?create=1&sku_id=X&location_id=Y
  // lets the Stock Lookup page jump straight into a pre-filled create dialog.
  useEffect(() => {
    if (searchParams.get('create') === '1') {
      const sku_id = searchParams.get('sku_id') || '';
      const variant_id = searchParams.get('variant_id') || '';
      const location_id = searchParams.get('location_id') || '';
      setPrefill({ sku_id, variant_id, location_id });
      setEditing(null);
      setShowForm(true);
      // Drop the params so a refresh doesn't re-open the dialog.
      const copy = new URLSearchParams(searchParams);
      ['create', 'sku_id', 'variant_id', 'location_id'].forEach((k) => copy.delete(k));
      setSearchParams(copy, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const remove = async (row) => {
    if (!window.confirm(`Remove fixed location ${row.sku?.sku_code} @ ${row.location?.location_code}?`)) return;
    try {
      await axios.delete(`${API}/api/fixed-locations/${row.id}`, { withCredentials: true });
      toast.success('Removed');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const replenishNow = async (row) => {
    try {
      await axios.post(`${API}/api/fixed-locations/${row.id}/replenish-now`, {}, { withCredentials: true });
      toast.success('Replenishment task created');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create task'); }
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-6" data-testid="fixed-locations-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b-2 border-slate-900 pb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Inventory</div>
            <h1 className="text-3xl font-bold">Fixed Locations</h1>
            <p className="text-xs text-muted-foreground mt-1">Map SKUs / variants to pick locations with min & max thresholds. When on-hand drops ≤ min, a replenishment task is created automatically.</p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" className="rounded-none font-bold" data-testid="fixed-loc-bulk-import-btn">
              <Link to="/csv-import?entity=fixed_locations">
                <UploadSimple size={16} className="mr-1" /> Bulk import CSV
              </Link>
            </Button>
            <Button
              data-testid="fixed-loc-new-btn"
              onClick={() => { setEditing(null); setShowForm(true); }}
              className="bg-brand hover:bg-brand/90 text-white rounded-none font-bold"
            >
              <Plus size={16} weight="bold" className="mr-1" /> New fixed location
            </Button>
          </div>
        </div>

        {err && (
          <div data-testid="fixed-loc-error" className="p-3 border border-rose-200 bg-rose-50 text-rose-700 text-sm flex items-center gap-2">
            <Warning weight="bold" /> {err}
          </div>
        )}

        <div className="border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted border-b border-border">
              <tr>
                {['SKU', 'Variant', 'Location', 'Zone', 'Min', 'Max', 'Priority', 'Active', ''].map((h) => (
                  <th key={h} className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">
                  No fixed locations yet. Click <span className="font-bold">New fixed location</span> to create one.
                </td></tr>
              )}
              {items.map((i) => (
                <tr
                  key={i.id}
                  data-testid={`fixed-loc-row-${i.sku?.sku_code}-${i.location?.location_code}`}
                  className="hover:bg-muted"
                >
                  <td className="px-3 py-2 font-mono font-bold">{i.sku?.sku_code || '—'}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {i.variant_id
                      ? [i.variant?.size, i.variant?.color, i.variant?.style].filter(Boolean).join(' · ') || 'Variant'
                      : <span className="text-muted-foreground italic">SKU default</span>
                    }
                  </td>
                  <td className="px-3 py-2 font-mono text-xs font-bold">{i.location?.location_code || '—'}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{i.location?.zone || '—'}</td>
                  <td className="px-3 py-2 font-mono text-amber-700 font-bold">{i.min_qty}</td>
                  <td className="px-3 py-2 font-mono text-emerald-700 font-bold">{i.max_qty}</td>
                  <td className="px-3 py-2 font-mono text-xs">{i.priority}</td>
                  <td className="px-3 py-2">
                    {i.is_active
                      ? <Badge className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-none text-[10px]">ACTIVE</Badge>
                      : <Badge className="bg-slate-400 text-white rounded-none text-[10px]">PAUSED</Badge>}
                  </td>
                  <td className="px-3 py-2 flex items-center gap-1">
                    <Button
                      size="sm" variant="outline" className="rounded-none h-7 text-[10px]"
                      onClick={() => replenishNow(i)}
                      data-testid={`fixed-loc-replenish-now-${i.sku?.sku_code}-${i.location?.location_code}`}
                    >
                      <Lightning weight="fill" size={12} className="mr-1 text-amber-500" /> Replenish now
                    </Button>
                    <Button
                      size="sm" variant="outline" className="rounded-none h-7 w-7 p-0"
                      onClick={() => { setEditing(i); setShowForm(true); }}
                      data-testid={`fixed-loc-edit-${i.id}`}
                    ><PencilSimpleLine size={14} /></Button>
                    <Button
                      size="sm" variant="outline" className="rounded-none h-7 w-7 p-0 text-rose-600"
                      onClick={() => remove(i)}
                      data-testid={`fixed-loc-delete-${i.id}`}
                    ><Trash size={14} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showForm && (
          <FixedLocationForm
            initial={editing}
            prefill={prefill}
            onClose={() => { setShowForm(false); setPrefill(null); }}
            onSaved={() => { setShowForm(false); setPrefill(null); load(); }}
          />
        )}
      </div>
    </Layout>
  );
}

// ============== CREATE / EDIT FORM ==============
function FixedLocationForm({ initial, prefill, onClose, onSaved }) {
  const isEdit = !!initial;
  const [skus, setSkus] = useState([]);
  const [variants, setVariants] = useState([]);
  const [locations, setLocations] = useState([]);
  const [sku_id, setSkuId] = useState(initial?.sku_id || prefill?.sku_id || '');
  const [variant_id, setVariantId] = useState(initial?.variant_id || prefill?.variant_id || '');
  const [location_id, setLocationId] = useState(initial?.location_id || prefill?.location_id || '');
  const [min_qty, setMin] = useState(initial?.min_qty ?? 5);
  const [max_qty, setMax] = useState(initial?.max_qty ?? 50);
  const [priority, setPriority] = useState(initial?.priority ?? 100);
  const [is_active, setIsActive] = useState(initial?.is_active !== false);
  const [err, setErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, l] = await Promise.all([
          axios.get(`${API}/api/skus?limit=500`, { withCredentials: true }),
          axios.get(`${API}/api/locations?limit=500`, { withCredentials: true }),
        ]);
        setSkus(Array.isArray(s.data) ? s.data : (s.data.data || []));
        setLocations(Array.isArray(l.data) ? l.data : (l.data.data || []));
      } catch (_) { /* silent */ }
    })();
  }, []);

  // Load variants when sku changes
  useEffect(() => {
    if (!sku_id) { setVariants([]); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/variants`, { params: { sku_id }, withCredentials: true });
        setVariants(Array.isArray(data) ? data : (data.data || []));
      } catch (_) { setVariants([]); }
    })();
  }, [sku_id]);

  const submit = async () => {
    setErr(null);
    if (!sku_id || !location_id) { setErr('SKU and Location are required'); return; }
    if (Number(min_qty) >= Number(max_qty)) { setErr('min_qty must be less than max_qty'); return; }
    setSubmitting(true);
    try {
      const payload = {
        sku_id, variant_id: variant_id || null, location_id,
        min_qty: Number(min_qty), max_qty: Number(max_qty),
        priority: Number(priority), is_active,
      };
      if (isEdit) {
        await axios.put(`${API}/api/fixed-locations/${initial.id}`, {
          min_qty: payload.min_qty, max_qty: payload.max_qty,
          priority: payload.priority, is_active: payload.is_active,
        }, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/fixed-locations`, payload, { withCredentials: true });
      }
      toast.success(isEdit ? 'Updated' : 'Fixed location created');
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent data-testid="fixed-loc-form-dialog" className="rounded-none max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin size={18} weight="fill" className="text-brand" />
            {isEdit ? 'Edit fixed location' : 'New fixed location'}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {isEdit ? 'Update thresholds only — SKU / variant / location are fixed after creation.' : 'Assign a SKU (or specific variant) to a pick location with min & max thresholds.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">SKU</Label>
            <select
              data-testid="fixed-loc-sku-select"
              disabled={isEdit}
              value={sku_id}
              onChange={(e) => { setSkuId(e.target.value); setVariantId(''); }}
              className="w-full border border-border px-3 py-2 font-mono text-sm disabled:bg-muted"
            >
              <option value="">— Select SKU —</option>
              {skus.map((s) => <option key={s.id} value={s.id}>{s.sku_code} — {s.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Variant (optional)</Label>
            <select
              data-testid="fixed-loc-variant-select"
              disabled={isEdit || !sku_id}
              value={variant_id}
              onChange={(e) => setVariantId(e.target.value)}
              className="w-full border border-border px-3 py-2 font-mono text-sm disabled:bg-muted"
            >
              <option value="">SKU default (all variants)</option>
              {variants.map((v) => (
                <option key={v.id} value={v.id}>
                  {[v.size, v.color, v.style].filter(Boolean).join(' · ') || v.barcode}
                </option>
              ))}
            </select>
            <div className="text-[10px] text-muted-foreground mt-1">Leave blank for an SKU-wide rule; choose a variant to override for that specific size/colour.</div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Pick location</Label>
            <select
              data-testid="fixed-loc-location-select"
              disabled={isEdit}
              value={location_id}
              onChange={(e) => setLocationId(e.target.value)}
              className="w-full border border-border px-3 py-2 font-mono text-sm disabled:bg-muted"
            >
              <option value="">— Select location —</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.location_code}{l.zone ? ` (Zone ${l.zone})` : ''}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-amber-600">Min</Label>
              <Input data-testid="fixed-loc-min" type="number" min={0} value={min_qty} onChange={(e) => setMin(e.target.value)} className="font-mono" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-emerald-600">Max</Label>
              <Input data-testid="fixed-loc-max" type="number" min={1} value={max_qty} onChange={(e) => setMax(e.target.value)} className="font-mono" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Priority</Label>
              <Input data-testid="fixed-loc-priority" type="number" min={1} max={1000} value={priority} onChange={(e) => setPriority(e.target.value)} className="font-mono" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch data-testid="fixed-loc-active" checked={is_active} onCheckedChange={setIsActive} />
            <Label className="text-xs text-muted-foreground">Active (scanner will consider this rule)</Label>
          </div>
          {err && (
            <div data-testid="fixed-loc-form-error" className="p-2 border border-rose-200 bg-rose-50 text-rose-700 text-xs flex items-center gap-2">
              <Warning weight="bold" /> {err}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button data-testid="fixed-loc-cancel" variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button
            data-testid="fixed-loc-submit"
            disabled={submitting}
            onClick={submit}
            className="bg-brand hover:bg-brand/90 text-white rounded-none font-bold"
          >
            {submitting ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
